import { useSelector } from 'react-redux'
import useSWR from 'swr'

import { endpoint } from '@library/endpoint'

import { GoustoHeaders } from 'apis/headers'
import { appendFlexOrderAttributes } from 'routes/Menu/apis/appendFlexOrderAttributes'
import { postFetcher } from 'routes/Menu/apis/fetch'
import { getOrderV2 } from 'routes/Menu/selectors/order'
import { transformOrderPricesV2ToOrderV1 } from 'routes/Menu/transformers/orderPricesV2ToV1'
import { getBasketRecipesCount, getBasketSlotId, getNumPortions } from 'selectors/basket'
import { canUseWindow } from 'utils/browserEnvironment'
import { SessionStorageKeys } from 'utils/processQuery'

import { useAuth } from '../auth'
import { useNumRecipesOverBasketLimit } from '../basket/useNumRecipesOverBasketLimit'
import type { OrderV2 } from '../orders/v2'

export type Pricing = {
  flatDiscountApplied: boolean
  items: {
    type?: string | null
    id?: string | null
    quantity?: string | null
    listPrice?: string | null
    discount?: string | null
    pricePerPortion?: string | null
  }[]
  promoCodeValid: boolean
  amountOff: string | null
  deliveryTotal: string | null
  grossTotal: string | null
  percentageOff: string | null
  pricePerPortion: string | null
  pricePerPortionDiscounted: string | null
  averageCostPerRecipe?: string
  averageCostPerPortion?: string
  productTotal: string | null
  promoCode: string | null
  recipeDiscount: string | null
  recipeTotal: string | null
  recipeTotalDiscounted: string | null
  surchargeCount: number
  surchargeTotal: string | null
  total: string | null
  totalDiscount: string | null
  vatCharged: string | null
  isDeliveryFree: boolean | null
}

const useGetPricing = (
  shouldFetch: boolean,
): { error: Error | null; data: Pricing | null; isValidating: boolean } => {
  const { accessToken, authUserId, isAuthenticated } = useAuth()
  const orderRequest = useSelector(getOrderV2) as unknown as OrderV2
  const numPortions = useSelector(getNumPortions)
  const orderRequestWithFlexAttributes = appendFlexOrderAttributes(
    orderRequest,
    isAuthenticated,
    numPortions,
  )
  let url = `${endpoint('order', 2)}/prices`
  const promo = (orderRequest as any)?.attributes?.prices?.promo_code
  if (promo) url += `?promo=${promo}`

  const testEmailAddress = canUseWindow()
    ? window.sessionStorage.getItem(SessionStorageKeys.GoustoTestEmailAddressKey)
    : null
  const headers = {
    ...(testEmailAddress ? { [GoustoHeaders.XGoustoTestEmailAddress]: testEmailAddress } : {}),
    ...(isAuthenticated ? { [GoustoHeaders.XGoustoFlexOrdersAllowed]: 'true' } : {}),
  }
  const params = shouldFetch
    ? [url, { data: orderRequestWithFlexAttributes }, accessToken, authUserId, headers]
    : null

  const {
    data: response,
    error,
    isValidating,
  } = useSWR<{ data: any; included: any }, Error>(
    params,
    ([requestUrl, requestData, token, userId, requestHeaders]) =>
      postFetcher(requestUrl, requestData, token, userId, requestHeaders),
    {
      revalidateIfStale: false,
      revalidateOnFocus: false,
      revalidateOnReconnect: false,
    },
  )

  if (error && url) {
    return { error, data: null, isValidating }
  }

  return {
    error: null,
    data: response ? transformOrderPricesV2ToOrderV1(response).data : null,
    isValidating,
  }
}

/**
 * @typedef {Object} PricingHookResponse
 * @property {object} pricing - Can also be null or undefined, will return the pricing data if available
 * @property {boolean} isPending - will return the loading state of the fetch call.
 * @property {boolean} isValid - is true when there's sufficient data to query the `/pricing` endpoint and therefore receive real data.
 */

/**
 * Returns the current prices calculated on the base of the items in the basket and promocodes
 * Using SWR will trigger an api call only once for each set of params
 * @returns { PricingHookResponse }
 */
export const usePricing = (): {
  pricing?: Pricing | null
  isPending: boolean
  isValid: boolean
} => {
  const deliverySlotId = useSelector(getBasketSlotId)
  const recipesCount = useSelector(getBasketRecipesCount)
  const orderRequest = useSelector(getOrderV2)
  const components = orderRequest?.relationships?.components?.data
  const hasComponents = Array.isArray(components) && components.length > 0

  const numRecipesOverLimit = useNumRecipesOverBasketLimit()
  const shouldFetch =
    recipesCount > 1 && !!deliverySlotId && hasComponents && numRecipesOverLimit <= 0

  const { error, data, isValidating } = useGetPricing(shouldFetch)

  return {
    isPending: isValidating && !error && shouldFetch,
    isValid: shouldFetch,
    pricing: data,
  }
}
